
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { IndicesType } from '@/store/enums/TypeOfIndicesEnums';

  export default defineComponent({
    name: 'testimonials-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      let file;
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedType = ref<string>('');

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('testimonialsEditing'), [
          translate('indices'),
        ]);
      });

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const previewImage = (e) => {
        file = e.target.files[0];
        thumbnail.value = URL.createObjectURL(file);
      };

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        blogId: Yup.number().nullable().label('Blog ID'),
        nameEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        nameAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
        contentEn: Yup.string()
          .required(() => translate('EN_CONTENT_IS_REQUIRED_FIELD'))
          .label('English Content'),
        contentAr: Yup.string()
          .required(() => translate('AR_CONTENT_IS_REQUIRED_FIELD'))
          .label('Arabic Content'),
        avatar: Yup.string().nullable().label('Avatar'),
        likesCount: Yup.number().nullable().label('Likes Count'),
        okayCount: Yup.number().nullable().label('Views Count'),
        type: Yup.number()
          .required(() => translate('TYPE_IS_REQUIRED_FIELD'))
          .label('Type'),
      });

      const { data } = await store.dispatch(
        Actions.GET_TESTIMONIAL,
        route.params.id
      );
      const testimonial = ref<any>(data.data);

      selectedType.value = String(testimonial.value.type);

      if (testimonial.value.avatar) thumbnail.value = testimonial.value.avatar;

      if (testimonial.value.publishDate) {
        const date = new Date(testimonial.value.publishDate);
        testimonial.value.publishDate = date.toISOString().slice(0, 16);
      }

      const onSubmitUpdate = async (values) => {
        delete values.avatar;
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (values.publishDate)
          values.publishDate = new Date(values.publishDate);

        if (selectedType.value) values.type = Number(selectedType.value);

        delete values.id;
        delete values.createdAt;

        await store.dispatch(Actions.UPDATE_TESTIMONIAL, {
          id: route.params.id,
          data: values,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (file) {
          const avatarFormData = new FormData();
          avatarFormData.append('logo', file);

          await store.dispatch(Actions.UPLOAD_TESTIMONIAL_AVATAR, {
            data: avatarFormData,
            id: route.params.id,
          });
        }

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_TESTIMONIAL'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'testimonials-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        testimonial,
        thumbnail,
        file,
        previewImage,
        IndicesType,
        selectedType,
      };
    },
  });
